// The website's What-we-do rows, generated by scripts/sync-capabilities.mjs
// from Bison-Office/bisoncommerce-website `main`, lib/services/*.ts, last
// changed upstream at commit 3e18c75. Do not edit: change the site, merge to
// its `main`, and run `npm run sync:sources`. capabilities-canonical.ts
// groups these into the Capabilities tour. A scene id the room has not
// vendored fails the type check: run the scene sync first.
import type { SceneId } from "../scenes/ids";

export const SITE_COMMIT = "3e18c75";

export type SiteRow = { key: string; title: string; body: string; scene?: SceneId };

export const SITE_ROWS = {
    "advertising": [
        {
            "key": "multi-marketplace",
            "title": "Multi-marketplace advertising",
            "body": "We run campaigns wherever your products sell. Led by Amazon and Walmart, extending to eBay, Wayfair, TikTok Shop, and Google, with the department-store and home-improvement retail media networks added as access opens.",
            "scene": "ChannelAdsStage"
        },
        {
            "key": "campaign-strategy",
            "title": "Automated campaign strategy management",
            "body": "Every product gets a portfolio of advertising strategies, not one campaign: product discovery ads that learn which searches sell, keyword ads that win your category, a brand banner at the top of search, ads on competitor product pages, retargeting that re-engages interested shoppers, and defense of your own listings. Each strategy adds sales the others can't reach, so the combination compounds into more conversion, not more cost. Built and managed end to end by our in-house team.",
            "scene": "CampaignPortfolio"
        },
        {
            "key": "bid-budget",
            "title": "Automated bid and budget optimization",
            "body": "Continuously tuned to defend your top performers and cut wasted spend, so the budget follows what's actually working.",
            "scene": "BidBudgetOptimization"
        },
        {
            "key": "promo-sync",
            "title": "Coordinated Advertising and Promo Strategy",
            "body": "Promotions, pricing, and advertising are planned together on one calendar. Your core campaigns run all the time. When a deal or coupon goes live, the price changes with it and we layer the right campaigns on top for exactly that window, a bid and budget lift, brand banners, and competitor targeting, then retargeting follows the shoppers the offer brought in. Nothing gets reshuffled, every click lands on the strongest price, and the lift sticks after the promotion ends.",
            "scene": "PromoSync"
        },
        {
            "key": "dayparting",
            "title": "Spend when shoppers buy",
            "body": "Shoppers browse at every hour, but they buy in a much narrower window, and the clicks outside it mostly go to waste. Our bids rise and fall through the day on each product's own hour-by-hour conversion history, so the same budget lands on the hours that produce orders. You spend nothing extra and get more sales from every advertising dollar.",
            "scene": "Dayparting"
        },
        {
            "key": "launch",
            "title": "New SKU launch expertise",
            "body": "A new SKU, or a whole new product category, launches with no sales history, no reviews, and no rank. We concentrate advertising to build early sales velocity and reviews, so the listing climbs organic rank fast, then taper spend as it takes hold. It is how we take a product from zero to established, and how we open new categories for your brand.",
            "scene": "LaunchRamp"
        }
    ],
    "content-optimization": [
        {
            "key": "healthscore",
            "title": "Listing Healthscore",
            "body": "Every listing scored for quality, completeness, and consistency, so you always know which products are leaving exposure on the table. The score is the metric we manage against: as we optimize, you watch it climb ASIN by ASIN, and see exactly where the catalog still has room to run.",
            "scene": "ListingHealthscore"
        },
        {
            "key": "review-analysis",
            "title": "Customer review analysis",
            "body": "We scrape and read the reviews to find the gaps the listing can't see: where the copy or images set the wrong expectation, and where shoppers are flagging the product itself, the packaging, or the delivery experience. Listing fixes go straight back into the page; genuine product and packaging issues come back to you. Fewer surprised buyers means fewer one-star reviews and a seller rating that climbs over time.",
            "scene": "ReviewAnalysis"
        },
        {
            "key": "competitor-pricing",
            "title": "Competitor pricing analysis",
            "body": "We benchmark each listing against the products winning the category: where their content, images, and reviews are pulling ahead, and where price is costing you the buy box or the click. You see exactly who you're up against and what it takes to out-position them, so the listing and the offer can respond instead of guess.",
            "scene": "CompetitorPricing"
        },
        {
            "key": "keywords",
            "title": "Keyword optimization",
            "body": "Selling points written around the terms shoppers actually use. We pull your organic keyword rankings, the keywords competitors are winning, and the terms your ad campaigns are already buying, then build them into titles, bullets, and backend search fields so organic and paid reinforce the same words instead of competing.",
            "scene": "KeywordEditor"
        },
        {
            "key": "image-enrichment",
            "title": "AI image enrichment",
            "body": "A full gallery built from a single product photo: room scenes that place the product in a real space, dimensional shots with measurements, infographics that call out features and materials, and highlight images that lead with the one detail that sells, all generated in an instant and ready to drop into the listing.",
            "scene": "ImageEnrichment"
        },
        {
            "key": "infographics",
            "title": "Infographic creation",
            "body": "Your brand, your images, and your selling points go in; finished infographic images come out. We generate callout graphics, dimension diagrams, and feature highlights from the photography you already have, styled to your brand guidelines. Shoppers get the key facts at a glance instead of digging for them, and the listing converts better for it.",
            "scene": "InfographicCreation"
        },
        {
            "key": "aplus",
            "title": "A+ content creation",
            "body": "Engaging, on-brand A+ content that boosts searchability and lifts conversion on the detail page. Every module stays in your voice and within your brand guidelines, so a richer page never becomes an off-brand one.",
            "scene": "AplusCreation"
        }
    ],
    "customer-support": [
        {
            "key": "responsive",
            "title": "Responsive support on every channel",
            "body": "Our support team answers phones, marketplace messages, and email across every channel you sell on, seven days a week, and 99.8% of calls are answered on the first dial. A team of 15+ specialists, backed by our own support technology, handles every buyer professionally and to each marketplace's standard.",
            "scene": "SupportLoopStage"
        },
        {
            "key": "resolution",
            "title": "Quick issue resolution",
            "body": "Sub-15-minute average first response, and every issue is owned by one person to closure instead of bounced between systems or left to escalate. Delivery, damage, and order problems get resolved before they cost you a rating.",
            "scene": "IssueResolution"
        },
        {
            "key": "compliant",
            "title": "Marketplace-compliant support",
            "body": "Marketplace rules change constantly, from return windows and refund timing to packaging, labels, and how fast messages must be answered. Our team tracks every policy update and applies it the moment it lands, so customer conversations already follow the newest requirements. Your account health stays green and your listings stay active.",
            "scene": "PlaybookRewrite"
        },
        {
            "key": "reviews",
            "title": "Customer review monitoring and response",
            "body": "Ratings and reviews watched across every channel and responded to fast, with recurring themes fed back into your listings and products so problems get fixed at the source.",
            "scene": "ReviewMonitoring"
        },
        {
            "key": "tailored",
            "title": "Support tailored to your business",
            "body": "Your warranty terms, your escalation rules, your voice. We build a playbook per brand and service buyers by it, so every conversation sounds like you.",
            "scene": "BrandPlaybook"
        },
        {
            "key": "transparent",
            "title": "Transparent customer support",
            "body": "Every damage claim lands in your supplier portal with the customer's actual photos, alongside return rates, claim data, and what buyers are saying about your products. It is information most resellers never pass along. We share the same view we work from, so you always know exactly how your products are doing.",
            "scene": "PortalStage"
        }
    ],
    "direct-sales-channels": [
        {
            "key": "new-demand",
            "title": "We create new demand",
            "body": "Shoppers find your products through channels the big marketplaces never reach: Google search, social feeds on Facebook, Instagram, Pinterest, and TikTok, affiliate content, and AI shopping assistants like ChatGPT. We run and fund that marketing, funnel the traffic to our own storefronts, and the orders stack on top of your marketplace sales; incremental demand you would not otherwise see.",
            "scene": "NewDemandStage"
        },
        {
            "key": "high-intent",
            "title": "High-intent buyers convert faster",
            "body": "A shopper who lands on a furniture or nursery storefront is already looking for exactly that. Each site is built around one type of buyer, so the navigation, search, and merchandising speak that buyer's language and your products are easy to find and compare. A focused range, clear content, and a fast checkout turn that intent into an order quicker than a crowded everything-store search page ever could.",
            "scene": "TailoredStorefront"
        },
        {
            "key": "brand-elevation",
            "title": "A storefront that elevates your brand",
            "body": "Every Bison storefront is built to the standard your own site would be held to: hero banner campaigns for your brand, rich product pages with photography that sells a considered purchase, featured-brand placement, and customer reviews on display. Your products appear the way you would present them yourself, not squeezed into a marketplace template. The first impression a direct shopper gets of your brand is one you would sign off on.",
            "scene": "StorefrontStage"
        },
        {
            "key": "returning-customers",
            "title": "Steady demand from returning customers",
            "body": "A marketplace sale ends at the order; a storefront sale starts a relationship. Shoppers who buy from our storefronts return to them, through email, remarketing, and a site they already know, and your brand is waiting when they do. Years of that returning traffic is why direct channel revenue builds steadily month after month instead of spiking and fading with an algorithm.",
            "scene": "ReturningCustomersStage"
        }
    ],
    "fulfillment-logistics": [
        {
            "key": "rates",
            "title": "Our competitive shipping rates help you sell more",
            "body": "Our negotiated ground and freight rates beat what suppliers can get on their own. Marketplaces build shipping into the product price, so our lower freight makes every listing 10-30% cheaper. That sharper price lifts your search rank and wins more sales.",
            "scene": "CarrierRateCompare"
        },
        {
            "key": "big-bulky",
            "title": "Big & bulky specialists",
            "body": "A network engineered for heavy, oversized freight across ground parcel and LTL, not just small parcel. Your catalog ships on infrastructure most sellers simply can't match.",
            "scene": "NationwideStage"
        },
        {
            "key": "on-time",
            "title": "On-time delivery, every time",
            "body": "A 99% on-time ship rate across 400,000+ orders a year, with delivery promises set, tracked, and met. Reliable delivery is what keeps your marketplace account health green and your reviews clean.",
            "scene": "DeliveryTrackStage"
        },
        {
            "key": "regional-speeds",
            "title": "Regional ship speeds",
            "body": "We promise ground and freight ship speeds region by region, on every marketplace you sell through. Shoppers near your warehouses see a 2-day promise instead of one nationwide estimate, so your regional fulfillment coverage turns into shorter lead times and more competitive listings that win more sales.",
            "scene": "ShipSpeedStage"
        },
        {
            "key": "damage-returns",
            "title": "Low damage and return rates",
            "body": "Packaging-aware shipping keeps breakage down at the source, and when a return or claim does happen we work it end to end, so damage and returns stop quietly turning your profit into claims.",
            "scene": "ReplacementStage"
        }
    ],
    "integrations": [
        {
            "key": "ai-automation",
            "title": "AI automation in everything we do",
            "body": "Our own engineering and data-science team builds AI into the connection: attribute mapping and content optimization at catalog scale, real-time compliance monitoring, automated ticket triage, machine-learning demand models that feed the pricing engine, and anomaly detection that flags a broken feed before it ever reaches your listings. The integration does not just move your data, it acts on it.",
            "scene": "AiCatalogStage"
        },
        {
            "key": "custom-integrations",
            "title": "Custom API and EDI integrations",
            "body": "A dedicated engineering team builds and owns custom API and EDI connections to your ERP, order management, or inventory system. There is no third-party middleware to manage or pay for; the same people who run the platform build and support every connection.",
            "scene": "CustomIntegrations"
        },
        {
            "key": "supplier-portal",
            "title": "Supplier portal",
            "body": "Your team gets one place to see everything: orders and shipments, inventory positions, and sales performance across every channel. No waiting on reports or digging through emails, the portal shows what is happening as it happens.",
            "scene": "PortalStage"
        },
        {
            "key": "one-connection",
            "title": "One connection, every marketplace",
            "body": "One integration covers every channel. We build and maintain the connections to 14+ marketplaces, from Amazon and Walmart to Target, Home Depot, Lowe's, and Wayfair, so you never build or maintain a channel-by-channel integration yourself.",
            "scene": "ConnectionStage"
        },
        {
            "key": "logistics-coordination",
            "title": "Automated logistics coordination",
            "body": "Orders route straight into our carrier network. Parcel and LTL freight are rated and booked automatically, labels and BOLs generate without a manual touch, and every tracking event flows back to the marketplace and your systems the moment the carrier reports it.",
            "scene": "CarrierBookingStage"
        },
        {
            "key": "heavy-lifting",
            "title": "We do the technical heavy lifting",
            "body": "Going live is a configuration, not a rebuild, and it never becomes an IT project on your side. You share your product data and marketplace access, then approve go-live. Our team handles everything in between: the API connection, AI-assisted attribute mapping, order validation, inventory and cancellation logic, and marketplace compliance testing.",
            "scene": "FastOnboarding"
        }
    ],
    "marketplace-management": [
        {
            "key": "listings",
            "title": "Multiple marketplace listing management",
            "body": "Your copy, images, and product assortment all live in one catalog that we manage for you. Every listing update lands on every marketplace at the same moment, and new products go live across all your channels in a single push.",
            "scene": "ListingManagement"
        },
        {
            "key": "inventory",
            "title": "Real-time inventory sync",
            "body": "One shared pool of inventory sits behind every marketplace you sell on. A sale on any channel updates every other channel the moment it happens, so you never oversell, and restocks bring every marketplace back online at once.",
            "scene": "InventoryFlow"
        },
        {
            "key": "pricing",
            "title": "Pricing automation",
            "body": "Our pricing engine reprices thousands of SKUs automatically, modeling demand and pricing correlation. Margin floors and MAP rules apply to every change. We listen to your pricing recommendations and work closely with you to ensure our approach remains aligned with your brand strategy.",
            "scene": "ChannelPricingStage"
        },
        {
            "key": "fulfillment",
            "title": "Nationwide fulfillment and logistics",
            "body": "Your orders ship fast and on time from a network of 700+ warehouses nationwide. We move small orders by ground and big, bulky items by LTL freight, so every order reaches the customer's door wherever they are in the country.",
            "scene": "NationwideStage"
        },
        {
            "key": "buy-box",
            "title": "Buy Box management",
            "body": "Our team wins and holds the Buy Box by coordinating pricing, fulfillment, and account health together, not by pulling a single lever in isolation. We also monitor every product across all your marketplaces, catch suppressed or flagged listings as they happen, and work them until they are resolved, so your catalog stays available to buy and no sale is missed.",
            "scene": "BuyBoxStage"
        },
        {
            "key": "relationships",
            "title": "Marketplace relationship management",
            "body": "Over years and at a scale few sellers reach, we have built direct relationships and standing on every major marketplace. We open channels you have no relationship with today, resolve issues through contacts most sellers never get, and put your brand in front of category teams and merchandising programs.",
            "scene": "ChannelAccessStage"
        },
        {
            "key": "support",
            "title": "Responsive customer support",
            "body": "A dedicated team resolves customer issues across all your channels, seven days a week. Our AI-enabled automation and customer support specialists are trained on the unique customer service policies set by each marketplace. That responsive support protects your brand and keeps your listings active.",
            "scene": "IssueResolution"
        },
        {
            "key": "compliance",
            "title": "Listing compliance, product certificates, and compliant support",
            "body": "Every listing meets each marketplace's content and category requirements, and your product certificates and documentation stay current so a lapsed file never takes a listing down. Our customer service follows each marketplace's own policies. We handle the compliance work that keeps your account in good standing, so you never have to think about it.",
            "scene": "PlaybookRewrite"
        }
    ],
    "pricing-automation": [
        {
            "key": "repricing",
            "title": "Automated repricing at scale",
            "body": "Our pricing engine updates prices more than 3 million times a day across every marketplace we sell on. Thousands of SKUs adjust in real time as the market moves, so your listings are never left stale or mispriced.",
            "scene": "ChannelPricingStage"
        },
        {
            "key": "buy-box",
            "title": "Buy Box optimization",
            "body": "The Buy Box decides who gets the sale, and price is only one of the signals behind it. Our engine balances your price against delivery speed, availability, and seller performance to win and hold the featured offer, taking the sale without cutting deeper than the win requires.",
            "scene": "BuyBoxStage"
        },
        {
            "key": "suppression",
            "title": "Listing suppression prevention",
            "body": "Marketplaces quietly suppress a listing when its price is beaten on another channel, and a suppressed listing sells nothing. We monitor your catalog for suppressions daily, trace each one to the price that caused it, and reprice to bring the listing back, so your products stay buyable.",
            "scene": "PricingUniverse"
        },
        {
            "key": "profit",
            "title": "Products priced to fund growth",
            "body": "We price each product where units sold and margin per unit add up to the most contribution. That is rarely the lowest price, so your brand is never dragged into a race to the bottom. The contribution earned at that price funds the advertising and promotions that grow your sales.",
            "scene": "ProfitOptimization"
        },
        {
            "key": "regional",
            "title": "Regional pricing",
            "body": "Freight on big-and-bulky products swings hard with where it ships from and to, so one national price is wrong almost everywhere. Regional pricing sets each US delivery region's price against its real landed cost, staying sharp where you're close to a warehouse and protecting margin where the freight is expensive.",
            "scene": "RegionalPriceStage"
        },
        {
            "key": "map",
            "title": "MAP policy enforcement",
            "body": "Minimum advertised price only holds if someone is watching every channel. We monitor listings continuously for violations and surface them with the evidence to act, keeping price discipline across the catalog so automated repricing never undercuts the floor you set.",
            "scene": "MapWatchStage"
        }
    ],
    "returns": [
        {
            "key": "handled",
            "title": "We handle returns for you",
            "body": "From the buyer's first message to the resolved claim, one team owns the whole path: intake, refund or replacement, pickup, and disposition across the 700+ warehouse network. You never staff a returns desk.",
            "scene": "ReturnsQueueStage"
        },
        {
            "key": "replacements",
            "title": "Fast replacements",
            "body": "A replacement or refund issued fast keeps the customer whole before a bad moment becomes a bad review, protecting the rating, the listing, and the repeat purchase.",
            "scene": "ReplacementStage"
        },
        {
            "key": "damage-claims",
            "title": "Damage claims worked to closure",
            "body": "Big-and-bulky freight damage is the category's biggest leak. We file and work carrier claims to resolution, so damage costs land on the carrier where they belong, not on your margin.",
            "scene": "IssueResolution"
        },
        {
            "key": "compliant",
            "title": "Marketplace-compliant returns",
            "body": "Every return processed to each marketplace's policy and timelines, so return handling is never what dings your ratings, account health, or Buy Box eligibility.",
            "scene": "PlaybookRewrite"
        },
        {
            "key": "reasons",
            "title": "Return reason reporting",
            "body": "Every return and damage claim is logged with a reason, and the themes are reported back to you, so you can see exactly what drives returns and fix it at the source: packaging, listings, or product.",
            "scene": "PortalStage"
        },
        {
            "key": "second-life",
            "title": "A second life for your products",
            "body": "Returned product is inspected and routed through our recommerce and reverse-logistics partners for resale, or donated through our charity partnerships, recovering value and putting product back to work instead of into the loss column.",
            "scene": "SecondLifeStage"
        }
    ],
    "white-label": [
        {
            "key": "brand",
            "title": "We build the brand",
            "body": "Naming, identity, and positioning created around the product you manufacture, so the same product reaches shoppers your own brand doesn't, without eroding it. You make it; we own the go-to-market.",
            "scene": "BrandBuildStage"
        },
        {
            "key": "listing",
            "title": "We create the listing",
            "body": "Complete marketplace listings, A+ content, and a full image gallery generated from a single product photo, ready to convert from the first day the product is live.",
            "scene": "ListingCreation"
        },
        {
            "key": "incremental-sales",
            "title": "Generate new incremental sales",
            "body": "Advertising and merchandising run by the same in-house ad ops team that grows our established brands, building velocity and rank for the new brand from launch.",
            "scene": "DemandChart"
        },
        {
            "key": "channel-conflicts",
            "title": "Avoid channel conflicts",
            "body": "Grow online volume without stepping on your existing retail relationships. A white label sells alongside your brand's dealer and retail network instead of undercutting it, so ecommerce growth never comes at the cost of the partners you already have.",
            "scene": "SearchRanking"
        },
        {
            "key": "experiment",
            "title": "Freedom to experiment",
            "body": "Test pricing, content, positioning, and marketplace access on the white label without touching your brand's equity, MAP, or channel strategy. Every test runs at arm's length from your brand, so the wins carry over and the misses cost nothing.",
            "scene": "ExperimentImpact"
        },
        {
            "key": "gated-marketplaces",
            "title": "Reach gated marketplaces on day one",
            "body": "Our brands already carry authorization on gated channels like Target Plus and Kohl's. Your product lists there immediately under the white label instead of waiting in an approval queue, on shelves your flagship brand was never going to reach on its own.",
            "scene": "ChannelAccessStage"
        }
    ]
} satisfies Record<string, SiteRow[]>;
